import { useCallback, useEffect, useRef, useState } from "react";

// Minimal query cache: keyed results, shared invalidation, no external library.
const cache = new Map<string, { data: unknown; at: number }>();
const subs = new Map<string, Set<() => void>>();

export function invalidate(prefix: string) {
  for (const k of [...cache.keys()]) if (k.startsWith(prefix)) cache.delete(k);
  for (const [k, set] of subs) if (k.startsWith(prefix)) set.forEach((fn) => fn());
}

export function useQuery<T>(key: string | null, fn: () => Promise<T>, opts: { staleMs?: number } = {}) {
  const staleMs = opts.staleMs ?? 30_000;
  const cached = key ? (cache.get(key) as { data: T; at: number } | undefined) : undefined;
  const [data, setData] = useState<T | undefined>(cached?.data);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(!cached && !!key);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const run = useCallback(async () => {
    if (!key) return;
    setLoading(true);
    try {
      const d = await fnRef.current();
      cache.set(key, { data: d, at: Date.now() });
      setData(d);
      setError(null);
    } catch (e) {
      setError(e as Error);
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => {
    if (!key) return;
    const c = cache.get(key) as { data: T; at: number } | undefined;
    if (c) setData(c.data);
    if (!c || Date.now() - c.at > staleMs) run();
    const set = subs.get(key) ?? new Set();
    set.add(run);
    subs.set(key, set);
    return () => {
      set.delete(run);
    };
  }, [key, run, staleMs]);

  return { data, error, loading, reload: run };
}

export function useMutation<A extends unknown[], R>(fn: (...args: A) => Promise<R>) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const mutate = useCallback(
    async (...args: A) => {
      setBusy(true);
      setError(null);
      try {
        return await fn(...args);
      } catch (e) {
        setError(e as Error);
        throw e;
      } finally {
        setBusy(false);
      }
    },
    [fn],
  );
  return { mutate, busy, error };
}

export function useDebounced<T>(value: T, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function useHotkey(key: string, fn: () => void) {
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key.toLowerCase() === key) {
        e.preventDefault();
        fn();
      }
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [key, fn]);
}
